import type {
  FilterOptionsDto,
  VoterDto,
  VoterListResponse,
  VoterMapPointDto,
  VoterMapFilter,
} from '@signalvote/shared';
import { apiJson } from './apiFetch';
import type { VoterListParams } from '../../lib/queryKeys';
import { campaignHeaders, defineQuery } from '@/lib/data';

/**
 * Paginated voter list with filters. Every voter read declares its GET's reads, so a write naming
 * a voter, a tag or a tag assignment refreshes it with no key listed.
 */
export const useVoters = defineQuery({
  key: (params: VoterListParams) => ['voters', 'list', params],
  reads: 'GET /api/Voters',
  fetch: (params, { signal, campaignId }) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) value.forEach((v) => searchParams.append(key, String(v)));
      else searchParams.append(key, String(value));
    });
    return apiJson<VoterListResponse>(`/api/voters?${searchParams}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch voters',
    });
  },
});

export const useVoter = defineQuery({
  key: (voterId: string) => ['voters', 'detail', voterId],
  reads: 'GET /api/Voters/{id}',
  fetch: (voterId, { signal, campaignId }) =>
    apiJson<VoterDto>(`/api/voters/${voterId}`, { signal, headers: campaignHeaders(campaignId), error: 'Failed to fetch voter' }),
});

function mapPointsQuery(filters: VoterMapFilter | undefined): string {
  const searchParams = new URLSearchParams();
  if (filters) {
    if (filters.contactStatus) searchParams.append('contactStatus', filters.contactStatus);
    if (filters.zipCodes?.length) searchParams.append('zipCodes', filters.zipCodes.join(','));
    if (filters.party?.length) searchParams.append('party', filters.party.join(','));
    if (filters.voteFrequencyMin != null) searchParams.append('voteFrequencyMin', String(filters.voteFrequencyMin));
    if (filters.voteFrequencyMax != null) searchParams.append('voteFrequencyMax', String(filters.voteFrequencyMax));
    if (filters.support?.length) searchParams.append('support', filters.support.join(','));
    if (filters.education?.length) searchParams.append('education', filters.education.join(','));
    if (filters.conservativeDonor != null) searchParams.append('conservativeDonor', String(filters.conservativeDonor));
    if (filters.liberalDonor != null) searchParams.append('liberalDonor', String(filters.liberalDonor));
    if (filters.tagIds?.length) searchParams.append('tagIds', filters.tagIds.join(','));
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}

/** Every voter coordinate for map rendering (no pagination). */
export const useVoterMapPoints = defineQuery({
  key: (filters: VoterMapFilter | undefined) => ['voters', 'map-points', filters],
  reads: 'GET /api/Voters/map-points',
  staleTime: 5 * 60 * 1000,
  fetch: (filters, { signal, campaignId }) =>
    apiJson<VoterMapPointDto[]>(`/api/voters/map-points${mapPointsQuery(filters)}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch voter map points',
    }),
});

/** Filter options for the voter map filter dropdowns (distinct party/education values). */
export const useVoterFilterOptions = defineQuery({
  key: () => ['voters', 'filter-options'],
  reads: 'GET /api/Voters/filter-options',
  staleTime: 30 * 60 * 1000,
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<FilterOptionsDto>('/api/voters/filter-options', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch filter options',
    }),
});
